import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { User } from '@models/identity/user';
import { UsersService } from '@services/entity/users.service';
import { plainToClass } from 'class-transformer';
import { TableColumn } from '@models/tableColumn';
import { AbstractEntitiesTableComponent } from '@abstracts/entities/entities-table';
import '@angular/localize/init';
import { UserTableEntry } from '@dto/userTableEntry';
import { TableOptions } from '@models/tableOptions';
import { NgxSpinnerService } from 'ngx-spinner';
import { BillingMethodEnum } from '@enums/billingMethodEnum.enum';

@Component({
  selector: 'app-users-table',
  templateUrl: './users-table.component.html'
})
export class UsersTableComponent extends AbstractEntitiesTableComponent implements OnInit, OnDestroy {

  public className = 'UsersTableComponent';

  public entities: UserTableEntry[];
  public isOperatorView: boolean = false;
  public isSupporter: boolean = false;

  private customerId: number;
  public showAll: boolean = false;
  public isAddingEnabled: boolean = false;
  private sortParam;

  public tableColumns: TableColumn[] = [
    { columnDescription: $localize`:@@UsersTableColumn2:Vorname`, property: 'firstName' },
    { columnDescription: $localize`:@@UsersTableColumn3:Nachname`, property: 'lastName' },
    { columnDescription: $localize`:@@UsersTableColumn1:E-Mail`, property: 'email' },
    { columnDescription: $localize`:@@UsersTableColumn4:Tarif`, property: 'pricingName'},
    { columnDescription: $localize`:@@UsersTableColumn7:Benutzer kann laden`, property: 'requiredActionMessage', options: { isHTML: true } },
  ];

  public tableOptions: TableOptions = { hideActions: true, rowRouterLink: 'edit/{id}' };

  constructor(
    public usersService: UsersService, private spinner: NgxSpinnerService, injector: Injector) {
    super(usersService, injector);
  }

  ngOnInit(): void {
    this.showAll = this.activatedRoute.snapshot.data['showAll'];
    this.isOperatorView = this.activatedRoute.snapshot.data['isOperatorView'];
    this.isSupporter = this.authenticationService.userHasRole('Supporter');
    super.ngOnInit();
    this.tableFunctions.sortColumn = 'firstName';
    this.tableFunctions.sortDirection = 'asc';
  }

  async initializeEntities() {
    if (this.showAll) {
      this.isAddingEnabled = false;
      this.usersService.getUserTable().subscribe(result => {
        this.entities = plainToClass(UserTableEntry, result);
        super.setupTable();
      }, error => { });
      this.tableOptions = { hideActions: true, rowRouterLink: '{id}' };
      this.tableColumns = [
        { columnDescription: $localize`:@@UsersTableColumn2:Vorname`, property: 'firstName' },
        { columnDescription: $localize`:@@UsersTableColumn3:Nachname`, property: 'lastName' },
        { columnDescription: $localize`:@@UsersTableColumn4:E-Mail`, property: 'email' },
        { columnDescription: $localize`:@@UsersTableColumn5:Firma`, property: 'company' },
        { columnDescription: $localize`:@@UsersTableColumn6:Ansprechpartner`, property: 'contactPerson' },
      ];

    } else if (this.isOperatorView) {
      this.isAddingEnabled = true;
      this.customerId = this.activatedRoute.parent.snapshot.params.key;
      this.usersService.getUserTableOfCustomer(this.customerId).subscribe(result => {
        this.entities = plainToClass(UserTableEntry, result);
        this.getStripSubscriptionStatus()
        super.setupTable();
      }, error => {});

    } else {
      this.isAddingEnabled = true;
      this.usersService.getUserTableOfCurrentCustomer().subscribe(result => {
        this.entities = plainToClass(UserTableEntry, result);
        this.getStripSubscriptionStatus()
        super.setupTable();
      }, error => { });
    }
  }

  matches(entity: User, term: string) {
    return UsersService.matches(entity, term);
  }

  syncWhitelistForAllUsers() {
    this.spinner.show();
    this.usersService.syncLocalOcppWhitelistForAllUsers(this.customerId).subscribe(result => {
      this.spinner.hide();
    }, error => {
      this.spinner.hide();
    })
  }

  getStripSubscriptionStatus()
  {
    this.entities.forEach(user => {
      if (user.rfidCardsString.length < 1) {
        user.requiredActionMessage = `
        <div class="d-inline-flex align-items-center gap-2">
          <span class="rounded bg-warning px-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
              class="feather feather-rfid-card">
              <path d="M3,4h18c1.1,0,2,0.9,2,2v12c0,1.1-0.9,2-2,2H3c-1.1,0-2-0.9-2-2V6C1,4.9,1.9,4,3,4z"/>
              <path d="M5.6,10.4c3.5-3.5,9.2-3.5,12.7,0"/>
              <circle cx="12" cy="15.3" r="1"/>
              <path d="M16.1,12.6c-2.3-2.3-5.9-2.3-8.2,0"/>
            </svg>
          </span>
          <span>Keine RFID-Karte zugeordnet</span>
        </div>
        `
      } else if (user.allowedChargingStationsString.length < 1) {
        user.requiredActionMessage = `
        <div class="d-inline-flex align-items-center gap-2">
          <span class="rounded bg-warning px-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
              class="feather feather-charging-station">
              <path d="M3,22V4c0-1.1,0.9-2,2-2h7c1.1,0,2,0.9,2,2v18H3z" />
              <path d="M16,22H1" />
              <path d="M14,9c4,0,3,4,3,8s6,4,6,0s-3-4-3-10" />
              <path d="M22,5c0,0,0,2-2,2s-2-2-2-2h2H22z" />
              <path d="M18,4c0,0.6,0,1,0,1S18,4.6,18,4s0-1,0-1S18,3.4,18,4z" />
              <path d="M22,4c0,0.6,0,1,0,1S22,4.6,22,4s0-1,0-1S22,3.4,22,4z" />
            </svg>
          </span>
          <span>Keine Ladestation zugeordnet</span>
        </div>
        `
      } else if (user.billingMethod == BillingMethodEnum.Automatic && user.stripePaymentMethodId == null && user.price != 0) {
        user.isStripeSubscripitionActive = true
        user.requiredActionMessage = `
        <div class="d-inline-flex align-items-center gap-2">
          <span class="rounded bg-danger text-white px-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
              class="feather feather-credit-card">
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
              <line x1="1" y1="10" x2="23" y2="10"></line>
            </svg>
          </span>
          <strong class="text-danger">Keine Zahlungsmethode hinterlegt</strong>
        </div>
        `
      } else if (user.billingMethod == null) {
        user.requiredActionMessage = `
        <div class="d-inline-flex align-items-center gap-2">
          <span class="rounded bg-success text-white px-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
              class="feather feather-check-square">
              <polyline points="9 11 12 14 22 4"></polyline>
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
            </svg>
          </span>
          <span>Laden erlaubt - Kein Tarif erforderlich</span>
        </div>
        `
        user.isStripeSubscripitionActive = user.stripePaymentMethodId != null
      }   else {
        user.requiredActionMessage = `
        <div class="d-inline-flex align-items-center gap-2">
          <span class="rounded bg-success text-white px-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
              class="feather feather-check-square">
              <polyline points="9 11 12 14 22 4"></polyline>
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
            </svg>
          </span>
          <span>Laden erlaubt mit Tarif: '${user.pricingName}'</span>
        </div>
        `
        user.isStripeSubscripitionActive = user.stripePaymentMethodId != null
      }
    })
  }
}
